<template>
  <div class="mv-root">

    <!-- ═══ NOTIFICAÇÕES IN-APP (overlay fixo) ═══ -->
    <MobileNotifStack
      :notifs="activeNotifs"
      :ttl-ms="NOTIF_TTL"
      @open="openChatById"
      @dismiss="dismissNotif"
    />

    <!-- ═══════════════ TELA: LISTA ═══════════════ -->
    <template v-if="screen === 'list'">
      <MobileConvList
        :auth-user="authUser"
        :data-stale="dataStale"
        :loading="loading"
        :has-conversations="conversations.length > 0"
        :countdown="countdown"
        :query="query"
        :filtered-convs="filteredConvs"
        :logo-url="logoUrl"
        :preview-text="previewText"
        :periodo="periodo"
        @update:query="query = $event"
        @update:periodo="setPeriodo"
        @logout="handleLogout"
        @refresh="loadData"
        @open-chat="openChat"
      />
    </template>

    <!-- ═══════════════ TELA: CHAT ═══════════════ -->
    <template v-else-if="screen === 'chat' && activeConv">
      <MobileChatHeader
        :conv="activeConv"
        :macro-sheet-open="macroSheetOpen"
        :assignee="currentAssignee"
        :current-labels="currentLabels"
        :updating-labels="updatingLabels"
        :active-sla-badge="activeSLABadge"
        :snooze-remaining-label="snoozeRemainingLabel"
        @back="screen = 'list'"
        @open-msg-search="chatMessagesRef?.openMsgSearch()"
        @open-macro-sheet="openMacroSheet"
        @toggle-status-menu="statusMenuOpen = !statusMenuOpen; snoozeMode = false; labelsOpen = false; agentSheetOpen = false"
        @open-agent-sheet="agentSheetOpen = true; statusMenuOpen = false; labelsOpen = false; fetchAgents()"
        @toggle-labels="labelsOpen = !labelsOpen; statusMenuOpen = false; agentSheetOpen = false"
        @toggle-label="toggleLabel"
      />

      <MobileChatMessages
        ref="chatMessagesRef"
        :processed-msgs="processedMsgs"
        :loading-msgs="loadingMsgs"
        :loading-more="loadingMore"
        :no-more-msgs="noMoreMsgs"
        :message-signature="authUser?.message_signature || ''"
        :conv-id="selConvId"
        :token="authUser?.access_token || ''"
        :contact-phone="activeConv.contact?.phone"
        :load-more-msgs="loadMoreMsgs"
        :window-closed="isWindowClosed"
        :assignee-id="currentAssignee?.id ?? null"
        :my-agent-id="authUser?.id ?? 0"
        :labels="currentLabels"
        :status="activeConv?.status"
        @message-created="onMessageCreated"
        @auto-assigned="onAutoAssigned"
        @labels-changed="(convId, labels) => { convLabels[convId] = labels }"
        @status-changed="onStatusChanged"
      />
    </template>
  </div>

  <!-- ═══ STATUS BOTTOM SHEET ═══ -->
  <MobileStatusSheet
    :open="statusMenuOpen && screen === 'chat'"
    :snooze-mode="snoozeMode"
    :current-status="activeConv?.status"
    :status-loading="statusLoading"
    :snooze-h="snoozeH"
    :snooze-m="snoozeM"
    @close="statusMenuOpen = false; snoozeMode = false"
    @change-status="changeStatus"
    @update:snooze-mode="snoozeMode = $event"
    @update:snooze-h="snoozeH = $event"
    @update:snooze-m="snoozeM = $event"
    @confirm-snooze="confirmSnooze"
  />

  <!-- ═══ AGENT BOTTOM SHEET ═══ -->
  <MobileAgentSheet
    :open="agentSheetOpen && screen === 'chat'"
    :agent-search="agentSearch"
    :agent-loading="agentLoading"
    :filtered-agents="filteredAgents"
    :current-assignee-id="currentAssignee?.id"
    @close="agentSheetOpen = false; agentSearch = ''"
    @update:agent-search="agentSearch = $event"
    @assign="doAssignAgent"
  />

  <!-- ═══ MACRO BOTTOM SHEET ═══ -->
  <MobileMacroSheet
    :open="macroSheetOpen && screen === 'chat'"
    :conv-id="selConvId"
    :conv="activeConv"
    :token="authUser?.access_token || ''"
    :account-id="authUser?.account_id"
    @close="closeMacroSheet"
    @run-done="fetchMsgs"
  />

  <!-- ═══ LABELS BOTTOM SHEET ═══ -->
  <MobileLabelsSheet
    :open="labelsOpen && screen === 'chat'"
    :all-labels="allLabels"
    :current-labels="currentLabels"
    :protected-titles="protectedLabelTitles"
    @close="labelsOpen = false"
    @toggle="toggleLabel"
  />
</template>

<script setup lang="ts">
import { ref, reactive, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import type { Conversation } from '@/types/dashboard'
import type { ChatMessage, ProcessedMsg, AgentItem, SLABadge, LabelItem } from '@/types/chat'
import type { MobileNotif } from '@/types/mobile'
import MobileNotifStack from '@/components/mobile/MobileNotifStack.vue'
import MobileConvList from '@/components/mobile/MobileConvList.vue'
import MobileStatusSheet from '@/components/mobile/MobileStatusSheet.vue'
import MobileAgentSheet from '@/components/mobile/MobileAgentSheet.vue'
import MobileLabelsSheet from '@/components/mobile/MobileLabelsSheet.vue'
import MobileMacroSheet from '@/components/mobile/MobileMacroSheet.vue'
import MobileChatHeader from '@/components/mobile/MobileChatHeader.vue'
import MobileChatMessages from '@/components/mobile/MobileChatMessages.vue'
import { onConversationMessage } from '@/composables/useChatwootMessageRealtime'
import { showPushNotification, requestNotificationPermission } from '@/services/notifications'
import { playNotificationSound } from '@/composables/useNotificationSound'
import { pendingChatId } from '@/composables/useChatTrigger'
import { isFluxoGeral, confirmAssignDuringFluxoGeral, confirmResolveConversation } from '@/composables/useHelpers'
import { useSLA } from '@/composables/useSLA'
import { useMessageWindow } from '@/composables/useMessageWindow'
import { N8N_TEAMS_URL } from '@/api/n8n'
import { snoozeConversation } from '@/services/snoozeService'
import {
  toggleConversationStatus, assignAgentViaSystem, logManualAssignment,
  fetchAccountLabels, setConversationLabels,
} from '@/services/conversationActionsService'
import { getProtectedLabelTitles } from '@/composables/useProtectedLabels'
import { resolveConversationSmart, type MacroActionContext } from '@/services/macroEngineService'
import { fetchMessages, fetchOlderMessages } from '@/services/messagesService'
import { useMessageCache } from '@/composables/useMessageCache'
import { listTeamMembersOrAgents } from '@/services/teamsService'
import { useLogo } from '@/composables/useLogo'
import { resolveRoleScope } from '@/composables/useAdmin'
import { periodSinceISO, type VisaoGeralPeriodo } from '@/composables/useDashboardEngine'
import { performLogout } from '@/composables/useAutoLogout'
import { filterOutBlacklisted } from '@/composables/useBlacklist'

const { logoUrl } = useLogo()
const WEBHOOK      = N8N_TEAMS_URL
const AUTO_REFRESH = 180
const WAIT_TOL_MIN = 1       // minutos sem resposta para notificar
const NOTIF_TTL    = 7000    // ms até auto-dismiss

const store  = useStore()
const router = useRouter()

const authUser = computed(() => store.getters.authUser)

const userTeamIds = computed<number[]>(() => store.getters.userTeamIds)

const { getProfileForFluxo: getSLAProfile, loadProfiles } = useSLA()

/* ── State ─────────────────── */
const screen        = ref<'list' | 'chat'>('list')
const query         = ref('')
const loading       = ref(false)
const countdown     = ref(AUTO_REFRESH)
const conversations = ref<Conversation[]>([])
// Mesmo filtro "Esta semana"/"Mês" do desktop (useDashboardEngine.ts) — "Esta
// semana" como padrão, igual lá (visaoGeralPeriodo começa em 'week').
const periodo = ref<VisaoGeralPeriodo>('week')
// Singleton de módulo (useMessageCache.ts) — compartilhado com ConvDrawer.vue,
// FocoView.vue e FloatChatWindow.vue, pra mesma conversa aberta em mais de um
// lugar não buscar o histórico 2x.
const { msgCache }  = useMessageCache()
const loadingMsgs   = ref(false)
const loadingMore   = ref(false)
const noMoreMsgs    = ref(false)
const selConvId     = ref<number | null>(null)
const chatMessagesRef = ref<InstanceType<typeof MobileChatMessages> | null>(null)
let countdownId:          ReturnType<typeof setInterval> | null = null
let pollTimer:            ReturnType<typeof setInterval> | null = null
let _nowTimer:            ReturnType<typeof setInterval> | null = null
let currentFetchController: AbortController | null = null

const nowSec         = ref(Math.floor(Date.now() / 1000))
const dataStale      = ref(false)
const statusMenuOpen = ref(false)
const snoozeMode     = ref(false)
const snoozeH        = ref(1)
const snoozeM        = ref(0)
const statusLoading  = ref(false)
const allLabels      = ref<LabelItem[]>([])
const labelsOpen     = ref(false)
const updatingLabels = ref(false)
const convLabels     = reactive<Record<number, string[]>>({})
const protectedLabelTitles = computed(() => getProtectedLabelTitles())

/* ── Atribuição de agente ───── */
const agentSheetOpen = ref(false)
const agentLoading   = ref(false)
const teamAgents     = ref<AgentItem[]>([])
const agentSearch    = ref('')
let   _agentTeamId   = -1

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase().trim()
  return q ? teamAgents.value.filter(a => a.name.toLowerCase().includes(q)) : teamAgents.value
})

const currentAssignee = computed(() =>
  selConvId.value != null ? (activeConv.value?.assignee ?? null) : null
)

async function fetchAgents() {
  const teamId = activeConv.value?.team?.id ?? null
  if (teamId === _agentTeamId && teamAgents.value.length) return
  agentLoading.value = true
  try {
    teamAgents.value = await listTeamMembersOrAgents(authUser.value?.access_token || '', teamId)
    _agentTeamId = teamId ?? -999
  } catch { /* silencioso */ } finally {
    agentLoading.value = false
  }
}

async function doAssignAgent(agentId: number | null) {
  if (!selConvId.value) return
  if (agentId && isFluxoGeral(activeConv.value?.fluxo_id)) {
    if (!(await confirmAssignDuringFluxoGeral())) return
  }
  agentSheetOpen.value = false
  agentSearch.value = ''
  const convId = selConvId.value
  const prev = activeConv.value?.assignee ?? null
  const agent = agentId ? teamAgents.value.find(a => a.id === agentId) ?? null : null
  const newAssignee = agent
    ? { id: agent.id, name: agent.name, email: '', role: 'agent', thumbnail: agent.thumbnail, availability: 'online' as const }
    : null
  const idx = conversations.value.findIndex(c => c.id === convId)
  if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], assignee: newAssignee }
  try {
    const ok = await assignAgentViaSystem(convId, agentId)
    if (!ok) {
      if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], assignee: prev }
    } else if (agent && idx !== -1 && authUser.value) {
      logManualAssignment(convId, agent.id, agent.name, conversations.value[idx].contact.name, authUser.value.id, authUser.value.name)
    }
  } catch {
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], assignee: prev }
  }
}

/* ── Macros ─────────────────── */
const macroSheetOpen = ref(false)

function openMacroSheet() {
  macroSheetOpen.value = true
  statusMenuOpen.value = false
  agentSheetOpen.value = false
  labelsOpen.value     = false
}

function closeMacroSheet() {
  macroSheetOpen.value = false
}

/* ── Notificações ───────────── */
const activeNotifs        = ref<MobileNotif[]>([])
const prevUnreadMap       = new Map<number, number>()   // convId → unread_count anterior
const notifiedWaitCycles  = new Map<number, number>()   // convId → ciclo notificado
const notifTimers         = new Map<string, ReturnType<typeof setTimeout>>()
const initialLoadDone     = ref(false)                   // evita notificar no primeiro carregamento

function addNotif(n: MobileNotif) {
  // evita duplicatas do mesmo tipo+conversa ao mesmo tempo
  const dup = activeNotifs.value.find(x => x.convId === n.convId && x.type === n.type)
  if (dup) dismissNotif(dup.uid)

  if (activeNotifs.value.length >= 3) dismissNotif(activeNotifs.value[0].uid)
  activeNotifs.value.push(n)

  // vibração (se suportada)
  if ('vibrate' in navigator) navigator.vibrate(n.type === 'message' ? [80, 30, 80] : [120])

  const timer = setTimeout(() => dismissNotif(n.uid), NOTIF_TTL)
  notifTimers.set(n.uid, timer)
}

function dismissNotif(uid: string) {
  const idx = activeNotifs.value.findIndex(n => n.uid === uid)
  if (idx !== -1) activeNotifs.value.splice(idx, 1)
  const t = notifTimers.get(uid)
  if (t) { clearTimeout(t); notifTimers.delete(uid) }
}

function checkNotifications(convs: Conversation[]) {
  if (!initialLoadDone.value) {
    // primeiro carregamento: apenas armazena os valores base, sem notificar
    convs.forEach(c => {
      prevUnreadMap.set(c.id, c.unread_count)
    })
    initialLoadDone.value = true
    return
  }

  const nowSecs = Date.now() / 1000

  // Remove ciclos de conversas que sumiram
  const currentIds = new Set(convs.map(c => c.id))
  for (const id of notifiedWaitCycles.keys()) {
    if (!currentIds.has(id)) notifiedWaitCycles.delete(id)
  }

  for (const conv of convs) {
    const prevUnread = prevUnreadMap.get(conv.id) ?? 0

    // ── Nova mensagem do cliente ────────────────────────────── (só status aberto)
    if (conv.unread_count > prevUnread && conv.status === 'open') {
      const newCount = conv.unread_count - prevUnread
      showPushNotification(
        conv.contact.name,
        `${newCount} nova${newCount > 1 ? 's' : ''} mensagem${newCount > 1 ? 's' : ''}`,
        `${window.location.origin}/?conv=${conv.id}`,
        `msg-${conv.id}`,
        conv.id,
      )
      // In-app: só notifica se o usuário não está vendo essa conversa agora
      if (conv.id !== selConvId.value) {
        addNotif({
          uid:         `msg-${conv.id}-${Date.now()}`,
          type:        'message',
          convId:      conv.id,
          contactName: conv.contact.name,
          desc:        `${newCount} nova${newCount > 1 ? 's' : ''} mensagem${newCount > 1 ? 's' : ''}`,
          team:        conv.team?.name ?? null,
        })
      }
    }

    // Atualiza mapa de não lidas
    prevUnreadMap.set(conv.id, conv.unread_count)

    // ── Aguardando resposta ─────────────────────────────────── (só status aberto)
    if (!conv.waiting_since || conv.waiting_since === 0) continue
    if (conv.status !== 'open') continue

    const waitMins = Math.floor((nowSecs - conv.waiting_since) / 60)
    const cycle = Math.floor(waitMins / WAIT_TOL_MIN)
    if (cycle < 1) continue

    const lastCycle = notifiedWaitCycles.get(conv.id) ?? -1
    if (cycle <= lastCycle) continue

    notifiedWaitCycles.set(conv.id, cycle)
    const waitLabel = waitMins < 60
      ? `${waitMins}min`
      : `${Math.floor(waitMins / 60)}h ${waitMins % 60 > 0 ? (waitMins % 60) + 'min' : ''}`.trim()

    showPushNotification(
      `${conv.contact.name} aguardando`,
      `Sem resposta há ${waitLabel}`,
      `${window.location.origin}/?conv=${conv.id}`,
      `wait-${conv.id}`,
      conv.id,
    )
    // In-app: aguardando (independente de qual conversa está aberta)
    if (conv.id !== selConvId.value) {
      addNotif({
        uid:         `wait-${conv.id}-${Date.now()}`,
        type:        'waiting',
        convId:      conv.id,
        contactName: conv.contact.name,
        desc:        `Aguardando há ${waitLabel}`,
        team:        conv.team?.name ?? null,
      })
    }
  }
}

/* ── Filtered convs ─────────── */
const myConvs = computed(() => {
  if (!authUser.value) return []
  const uid    = authUser.value.id
  // Regra absoluta do MobileView: conversa ainda no fluxo Geral do robô (ou
  // sem fluxo_id, mesmo default de isFluxoGeral) nunca aparece aqui, mesmo
  // pra quem tem escopo irrestrito (admin/hasTodos) — o mobile não tem UI
  // pra tratar Fluxo Geral (aviso de confirmação etc.), só o fluxo do bot já
  // resolvido/atribuído a um agente/time específico.
  const active = conversations.value.filter(c =>
    (c.status === 'open' || c.status === 'pending' || c.status === 'snoozed') && !isFluxoGeral(c.fluxo_id)
  )

  // Mesma cascata de regras geral.* usada em ConvDrawer.vue/DashboardView.vue —
  // a regra mais restritiva vence, para o mobile não ficar mais permissivo que o desktop.
  const scope = resolveRoleScope(uid, userTeamIds.value)

  if (scope.unrestricted) return active
  if (scope.hasSomenteAgente) {
    return active.filter(c => c.assignee?.id === uid)
  }
  if (scope.hasAgenteETime) {
    return active.filter(c => c.assignee?.id === uid || (scope.tids.length > 0 && c.team && scope.tids.includes(c.team.id)))
  }
  if (scope.hasTodos) return active
  // Fallback: mesmo comportamento de visaoSomenteAgente
  return active.filter(c => c.assignee?.id === uid)
})

const filteredConvs = computed(() => {
  const q = query.value.toLowerCase()
  if (!q) return myConvs.value
  return myConvs.value.filter(c =>
    c.contact.name.toLowerCase().includes(q) ||
    (c.contact.phone || '').toLowerCase().includes(q)
  )
})

const activeConv = computed(() =>
  selConvId.value != null ? myConvs.value.find(c => c.id === selConvId.value) ?? null : null
)

const processedMsgs = computed<ProcessedMsg[]>(() => {
  if (!selConvId.value) return []
  const msgs = (msgCache.value[selConvId.value] || [])
    .filter(m => m.message_type <= 2 && (m.content || m.attachments?.length))
  return msgs.map((msg, i) => {
    const next = msgs[i + 1]
    return { ...msg, showAvatar: !next || next.message_type !== msg.message_type }
  })
})

const { isWindowClosed } = useMessageWindow(processedMsgs, nowSec)

const snoozeRemainingLabel = computed<string | null>(() => {
  const conv = activeConv.value
  if (!conv || conv.status !== 'snoozed' || !conv.snoozed_until) return null
  const diff = conv.snoozed_until - nowSec.value
  if (diff <= 0) return null
  const h = Math.floor(diff / 3600)
  const m = Math.floor((diff % 3600) / 60)
  if (h > 0) return `${h}h ${m > 0 ? m + 'min' : ''}`
  if (m > 0) return `${m}min`
  return 'menos de 1min'
})

const currentLabels = computed<string[]>(() => {
  if (!selConvId.value) return []
  return convLabels[selConvId.value] ?? activeConv.value?.labels ?? []
})

const activeSLABadge = computed<SLABadge | null>(() => {
  const conv = activeConv.value
  if (!conv || (conv.status !== 'open' && conv.status !== 'pending')) return null
  const profile = getSLAProfile(conv.fluxo_id)
  if (!profile) return null
  const now = nowSec.value
  const candidates: { remaining: number; label: string; status: 'breached' | 'atRisk' }[] = []
  if (profile.resolutionTime > 0) {
    const deadline = conv.created_at + profile.resolutionTime * 60
    const remaining = deadline - now
    if (remaining <= 0) candidates.push({ remaining, label: 'Resolução', status: 'breached' })
    else if (profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60)
      candidates.push({ remaining, label: 'Resolução', status: 'atRisk' })
  }
  if (profile.firstResponseTime > 0 && conv.waiting_since > 0) {
    const deadline = conv.waiting_since + profile.firstResponseTime * 60
    const remaining = deadline - now
    if (remaining <= 0) candidates.push({ remaining, label: '1ª Resposta', status: 'breached' })
    else if (profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60)
      candidates.push({ remaining, label: '1ª Resposta', status: 'atRisk' })
  }
  if (!candidates.length) return null
  candidates.sort((a, b) => a.remaining - b.remaining)
  const worst = candidates[0]
  const abs = Math.abs(Math.floor(worst.remaining))
  const h = Math.floor(abs / 3600), m = Math.floor((abs % 3600) / 60), s = abs % 60
  const timeStr = worst.remaining <= 0
    ? `+${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : s + 's'}`
    : `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : s + 's'}`
  return { label: worst.label, time: timeStr, status: worst.status }
})

/* ── Snooze/Status/Labels helpers ─── */
function parseSnoozedUntil(v: any): number | null {
  if (!v) return null
  if (typeof v === 'string') return Math.floor(new Date(v).getTime() / 1000)
  if (typeof v === 'number') return v > 1e10 ? Math.floor(v / 1000) : v
  return null
}

async function changeStatus(status: string, snoozedUntil: number | null = null) {
  if (statusLoading.value || !activeConv.value) return
  if (status === 'resolved') {
    statusMenuOpen.value = false
    if (!(await confirmResolveConversation())) return
  }
  statusLoading.value = true
  statusMenuOpen.value = false
  snoozeMode.value = false
  const convId = activeConv.value.id
  try {
    let newStatus: string
    if (status === 'snoozed') {
      const result = await snoozeConversation(convId, authUser.value?.access_token || '', snoozedUntil)
      newStatus = result.status
    } else if (status === 'resolved') {
      const conv = activeConv.value
      const context: MacroActionContext = {
        contactPhone:       conv?.contact?.phone ?? null,
        contactName:        conv?.contact?.name  ?? null,
        contactEmail:       (conv?.contact as any)?.email ?? null,
        teamId:             conv?.team?.id   ?? null,
        teamName:           conv?.team?.name ?? null,
        conversationStatus: conv?.status ?? null,
        contactId:          conv?.contact?.id ?? null,
        assigneeId:         conv?.assignee?.id ?? null,
        assigneeName:       conv?.assignee?.name ?? null,
        labels:             conv?.labels ?? [],
        accountId:          authUser.value?.account_id ?? null,
        executedById:       authUser.value?.id ?? null,
        executedByName:     authUser.value?.name ?? null,
      }
      const outcome = await resolveConversationSmart(convId, authUser.value?.access_token || '', context)
      if (!outcome.ok) return
      newStatus = outcome.status ?? 'resolved'
    } else {
      const result = await toggleConversationStatus(convId, authUser.value?.access_token || '', status)
      if (result === null) return
      newStatus = result
    }
    const idx = conversations.value.findIndex(c => c.id === convId)
    if (idx !== -1) {
      conversations.value[idx] = { ...conversations.value[idx], status: newStatus, snoozed_until: snoozedUntil }
    }
    if (newStatus === 'resolved') {
      screen.value = 'list'
      selConvId.value = null
    }
  } catch (e) {
    console.error('[MobileView] changeStatus:', e)
  } finally {
    statusLoading.value = false
  }
}

function confirmSnooze() {
  const total = (snoozeH.value || 0) * 3600 + (snoozeM.value || 0) * 60
  const until = total > 0 ? Math.floor(Date.now() / 1000) + total : null
  changeStatus('snoozed', until)
}

async function fetchAllLabels() {
  if (allLabels.value.length) return
  try {
    allLabels.value = await fetchAccountLabels(authUser.value?.access_token || '')
  } catch { /* silencioso */ }
}

async function toggleLabel(label: string) {
  if (!selConvId.value || updatingLabels.value) return
  const convId = selConvId.value
  const current = [...(convLabels[convId] ?? activeConv.value?.labels ?? [])]
  const next = current.includes(label) ? current.filter(l => l !== label) : [...current, label]
  convLabels[convId] = next
  updatingLabels.value = true
  try {
    const result = await setConversationLabels(convId, authUser.value?.access_token || '', next)
    convLabels[convId] = result ?? current
  } catch {
    convLabels[convId] = current
  } finally {
    updatingLabels.value = false
  }
}

/* ── Load conversations ─────── */
async function loadData() {
  currentFetchController?.abort()
  currentFetchController = new AbortController()
  const { signal } = currentFetchController

  loading.value = true
  if (countdownId) clearInterval(countdownId)

  const delays = [2000, 4000]
  let lastErr: Error = new Error('Falha')

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command: 'get_conversations',
          timestamp: Date.now(),
          access_token: authUser.value?.access_token,
          since: periodSinceISO(periodo.value),
          // Mesma regra do Dashboard/Modo Foco (useDashboardEngine.ts): conversa
          // sem agente E sem time (só com o robô/Typebot/IA) nunca deve aparecer
          // pro agente — requer "Listar Chats" v9 no n8n, com degradação graciosa.
          exclude_unassigned_no_team: true,
        }),
        signal,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const raw = await res.json()
      const entry = Array.isArray(raw) ? raw[0] : raw
      const payload = entry?.data?.payload || entry?.payload || (Array.isArray(raw) ? raw : [])
      conversations.value = filterOutBlacklisted(payload.map((conv: any) => {
        const m = conv.meta || {}
        return {
          id: conv.id,
          status: conv.status || 'open',
          channel: m.channel || '',
          waiting_since: conv.waiting_since || 0,
          unread_count: conv.unread_count || 0,
          created_at: conv.created_at || 0,
          resolved_at: conv.resolved_at || (conv.status === 'resolved' ? (conv.updated_at || 0) : 0),
          contact: {
            id: m.sender?.id || 0,
            name: m.sender?.name || '—',
            phone: m.sender?.phone_number || m.sender?.email || '',
            thumbnail: m.sender?.thumbnail || '',
          },
          assignee: m.assignee ? {
            id: m.assignee.id,
            name: m.assignee.name || m.assignee.available_name || '—',
            email: m.assignee.email || '',
            role: m.assignee.role || 'agent',
            thumbnail: m.assignee.thumbnail || '',
            availability: m.assignee.availability_status || 'offline',
          } : null,
          team: m.team ? { id: m.team.id, name: m.team.name || `Time #${m.team.id}` } : null,
          labels: Array.isArray(conv.labels) ? conv.labels : [],
          fluxo_id: conv.custom_attributes?.fluxo_id ?? 1,
          priority: conv.priority || null,
          snoozed_until: parseSnoozedUntil(conv.snoozed_until),
          cpf:  conv.custom_attributes?.cpf  || null,
          cnpj: conv.custom_attributes?.cnpj || null,
        }
      }))

      checkNotifications(myConvs.value)
      dataStale.value = false

      countdown.value = AUTO_REFRESH
      countdownId = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          if (countdownId) clearInterval(countdownId)
          loadData()
        }
      }, 1000)

      loading.value = false
      return

    } catch (err: any) {
      if (err?.name === 'AbortError') { loading.value = false; return }
      lastErr = err
      if (attempt < 2) await new Promise<void>(r => setTimeout(r, delays[attempt]))
    }
  }

  console.error('[MobileView]', lastErr)
  dataStale.value = true

  // Mantém countdown mesmo sem dados para tentar novamente
  countdown.value = AUTO_REFRESH
  countdownId = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      if (countdownId) clearInterval(countdownId)
      loadData()
    }
  }, 1000)

  loading.value = false
}

function setPeriodo(p: VisaoGeralPeriodo) {
  if (periodo.value === p) return
  periodo.value = p
  loadData()
}

/* ── Messages ───────────────── */
// fetchMsgs() é chamado tanto pela carga inicial (openChat) quanto pelo poll
// de 8s e pelo gatilho de realtime (onConversationMessage) — os dois últimos
// disparam a cada poucos segundos/a cada mensagem, então só a carga inicial
// (sem cache ainda pra essa conversa) pode acender o spinner de tela cheia ou
// forçar scroll: senão a lista inteira "recarregava" visualmente a cada tick,
// mesmo quando não tinha nada novo (ver conversa — não era o WS/poll lentos,
// era isso ligando loadingMsgs/scrollBottom incondicionalmente).
async function fetchMsgs(convId: number) {
  const prevMsgs = msgCache.value[convId]
  const isInitialLoad = !prevMsgs
  if (isInitialLoad) loadingMsgs.value = true
  noMoreMsgs.value = false
  try {
    const messages = await fetchMessages(convId)
    if (messages !== null) {
      let hasNewMsg = isInitialLoad
      if (prevMsgs) {
        const prevIds = new Set(prevMsgs.map(m => m.id))
        hasNewMsg = messages.some(m => !prevIds.has(m.id))
        // Som só quando a mensagem nova é do contato (message_type 0 = incoming,
        // 1 = outgoing, 2 = atividade) — já excluído de isInitialLoad acima.
        if (messages.some(m => m.message_type === 0 && !prevIds.has(m.id))) playNotificationSound()
      }
      msgCache.value = { ...msgCache.value, [convId]: messages }
      if (hasNewMsg) {
        await nextTick()
        chatMessagesRef.value?.scrollBottom()
      }
    }
  } catch (e) {
    console.error('[MobileView] msgs:', e)
  } finally {
    loadingMsgs.value = false
  }
}

async function loadMoreMsgs() {
  const convId = selConvId.value
  if (!convId || loadingMore.value || noMoreMsgs.value) return
  const current = msgCache.value[convId] ?? []
  if (!current.length) return
  loadingMore.value = true
  const prevHeight = chatMessagesRef.value?.getScrollEl()?.scrollHeight ?? 0
  try {
    const result = await fetchOlderMessages(convId, current)
    if (result.noMore) { noMoreMsgs.value = true; return }
    if (result.messages === current) return
    msgCache.value = { ...msgCache.value, [convId]: result.messages }
    await nextTick()
    const el = chatMessagesRef.value?.getScrollEl()
    if (el) el.scrollTop = el.scrollHeight - prevHeight
  } catch (e) {
    console.error('[MobileView loadMore]', e)
  } finally {
    loadingMore.value = false
  }
}

async function openChat(conv: Conversation) {
  selConvId.value = conv.id
  screen.value = 'chat'
  statusMenuOpen.value = false
  labelsOpen.value = false
  snoozeMode.value = false
  prevUnreadMap.set(conv.id, 0)
  await fetchMsgs(conv.id)
  fetchAllLabels()
  if (pollTimer) clearInterval(pollTimer)
  // 25s em vez de 8s — o WS (onConversationMessage abaixo) já cobre o caso
  // normal (agente membro do inbox) na hora; isso aqui é só rede de segurança
  // pro caso documentado de atribuição cruzada (docs/planos/PLANO-REALTIME-ACTIONCABLE.md),
  // onde o evento nunca chega. Não dá pra tirar de vez sem regredir esse caso,
  // mas não precisa mais dos 8s agora que fetchMsgs() não pisca a tela quando
  // não há nada novo (ver comentário em fetchMsgs).
  pollTimer = setInterval(() => { if (selConvId.value) fetchMsgs(selConvId.value) }, 25000)
}

async function openChatById(convId: number) {
  const conv = myConvs.value.find(c => c.id === convId)
  if (conv) await openChat(conv)
}

// Abre a conversa apontada por uma notificação (clique no push do sistema)
watch(pendingChatId, async (id) => {
  if (id === null) return
  pendingChatId.value = null
  await openChatById(id)
})

watch(screen, (s) => {
  if (s !== 'chat' && pollTimer) { clearInterval(pollTimer); pollTimer = null }
})

// Fase 5 do plano de tempo real — trigger de refetch imediato quando chega
// mensagem via WS pra conversa aberta. Poll de 8s continua como rede de
// segurança (ver useChatwootMessageRealtime.ts).
let msgRealtimeUnsub: (() => void) | null = null
watch(selConvId, (convId) => {
  msgRealtimeUnsub?.()
  msgRealtimeUnsub = null
  if (convId) {
    msgRealtimeUnsub = onConversationMessage(convId, () => fetchMsgs(convId))
  }
})

// Se a conversa ativa desaparecer (resolvida por outro agente), volta à lista
watch(activeConv, (conv) => {
  if (!conv && screen.value === 'chat') {
    screen.value = 'list'
    selConvId.value = null
  }
})

/* ── Send ───────────────────── */
function onMessageCreated(msg: ChatMessage, convId: number) {
  if (!msgCache.value[convId]) msgCache.value[convId] = []
  msgCache.value[convId] = [...msgCache.value[convId], msg]
}

function onAutoAssigned(convId: number, agentId: number) {
  const me = authUser.value
  if (!me) return
  const idx = conversations.value.findIndex(c => c.id === convId)
  if (idx !== -1) conversations.value[idx] = {
    ...conversations.value[idx],
    assignee: { id: agentId, name: me.name, email: me.email || '', role: 'agent', thumbnail: me.avatar_url || '', availability: 'online' },
  }
}

function onStatusChanged(convId: number, status: string) {
  const idx = conversations.value.findIndex(c => c.id === convId)
  if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], status }
}

/* ── Logout ─────────────────── */
async function handleLogout() {
  if (countdownId) clearInterval(countdownId)
  if (pollTimer) clearInterval(pollTimer)
  notifTimers.forEach(clearTimeout); notifTimers.clear()
  await performLogout(store, router)
}

/* ── Helpers ────────────────── */
function previewText(convId: number): string {
  const msgs = msgCache.value[convId]
  if (!msgs?.length) return 'Toque para abrir…'
  const last = msgs[msgs.length - 1]
  if (last?.content) return last.content.slice(0, 55)
  if (last?.attachments?.length) {
    const t = last.attachments[0].file_type
    return t === 'image' ? '📷 Imagem' : t === 'audio' ? '🎵 Áudio' : t === 'video' ? '🎥 Vídeo' : '📎 Arquivo'
  }
  return '—'
}

/* ── Lifecycle ──────────────── */
onMounted(async () => {
  document.body.style.overflow = 'hidden'
  await requestNotificationPermission()
  loadData()
  loadProfiles()
  _nowTimer = setInterval(() => { nowSec.value = Math.floor(Date.now() / 1000) }, 10_000)
})

onUnmounted(() => {
  document.body.style.overflow = ''
  currentFetchController?.abort()
  if (countdownId) clearInterval(countdownId)
  if (pollTimer) clearInterval(pollTimer)
  if (_nowTimer) clearInterval(_nowTimer)
  msgRealtimeUnsub?.()
  notifTimers.forEach(clearTimeout); notifTimers.clear()
})
</script>

<style scoped>
/* ══ Root ══ */
.mv-root {
  position: fixed;
  inset: 0;
  background: #F7F7F7;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
  z-index: 10;
  overflow: hidden;
}
</style>
